import React, { useState, useEffect, useCallback } from 'react';
import Joyride, { ACTIONS, EVENTS, STATUS } from 'react-joyride';
import axios from 'axios';
import TUTORIAL_STEPS from './tutorialSteps';
import TutorialTooltip from './TutorialTooltip';
import TutorialContext from './TutorialContext';

const TutorialTour = ({ currentUser, history, children }) => {
  const [run, setRun] = useState(false);
  const [stepIndex, setStepIndex] = useState(0);

  // Auto-start after 1s if user hasn't completed the tutorial
  useEffect(() => {
    if (currentUser?.hasCompletedTutorial) return;
    const timer = setTimeout(() => {
      if (history.location.pathname !== '/dashboard') {
        history.push('/dashboard');
      }
      setRun(true);
    }, 1000);
    return () => clearTimeout(timer);
  }, [currentUser?.hasCompletedTutorial, history]);

  const markCompleted = useCallback(async () => {
    if (!currentUser?.userId) return;
    try {
      await axios.patch(`/users/${currentUser.userId}`, { hasCompletedTutorial: true });
      if (currentUser.setHasCompletedTutorial) {
        currentUser.setHasCompletedTutorial(true);
      }
    } catch (err) {
      console.error('Failed to save tutorial completion:', err);
    }
  }, [currentUser]);

  const restart = useCallback(() => {
    if (history.location.pathname !== '/dashboard') {
      history.push('/dashboard');
    }
    setStepIndex(0);
    setTimeout(() => setRun(true), 600);
  }, [history]);

  const handleJoyrideCallback = useCallback((data) => {
    const { action, index, type, status } = data;

    // Tour finished or skipped
    if (status === STATUS.FINISHED || status === STATUS.SKIPPED) {
      setRun(false);
      markCompleted();
      const lastStep = TUTORIAL_STEPS[index];
      if (lastStep?.navigateAfter) {
        history.push(lastStep.navigateAfter);
      }
      return;
    }

    if (type === EVENTS.STEP_AFTER) {
      const currentStep = TUTORIAL_STEPS[index];
      const nextIndex = action === ACTIONS.PREV ? index - 1 : index + 1;
      const nextStep = TUTORIAL_STEPS[nextIndex];

      // Last step: finish the tour and navigate
      if (!nextStep) {
        setRun(false);
        markCompleted();
        if (currentStep?.navigateAfter) {
          history.push(currentStep.navigateAfter);
        }
        return;
      }

      const scrollContentToTop = () => {
        const el = document.querySelector('[data-tour-scroll="content"]');
        if (el) el.scrollTop = 0;
        window.scrollTo(0, 0);
      };

      // Cross-page navigation
      if (action === ACTIONS.NEXT && currentStep?.navigateAfter) {
        setRun(false);
        history.push(currentStep.navigateAfter);
        setTimeout(() => {
          scrollContentToTop();
          setStepIndex(nextIndex);
          setRun(true);
        }, 600);
      } else if (action === ACTIONS.PREV && nextStep?.page !== currentStep?.page) {
        setRun(false);
        history.push(nextStep.page);
        setTimeout(() => {
          scrollContentToTop();
          setStepIndex(nextIndex);
          setRun(true);
        }, 600);
      } else {
        setStepIndex(nextIndex);
      }
    }
  }, [history, markCompleted]);

  return (
    <TutorialContext.Provider value={{ restartTutorial: restart }}>
      {run && (
        <Joyride
          steps={TUTORIAL_STEPS}
          run={run}
          stepIndex={stepIndex}
          continuous
          showSkipButton
          disableOverlayClose
          disableCloseOnEsc={false}
          spotlightClicks={false}
          tooltipComponent={TutorialTooltip}
          callback={handleJoyrideCallback}
          styles={{
            options: {
              zIndex: 10000,
              arrowColor: '#ffffff',
            },
            overlay: {
              backgroundColor: 'rgba(0, 0, 0, 0.5)',
            },
          }}
          floaterProps={{
            disableAnimation: true,
          }}
        />
      )}
      {children}
    </TutorialContext.Provider>
  );
};

export default TutorialTour;
